import PropTypes from "prop-types";
import React from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FaGoogle, FaMicrosoft, FaYahoo, FaApple, FaDownload, FaCalendarPlus } from "react-icons/fa";
import { TiTimes } from "react-icons/ti";
import { formatDate, formatTime } from "src/utils/formatters";
import { useIsSmScreenDown } from "src/utils/windowUtils";

// Custom toggle component using forwardRef for Dropdown positioning
const CustomToggle = React.forwardRef(({ children, onClick, ariaLabel }, ref) => (
  <ToggleSpan
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    aria-label={ariaLabel}
  >
    {children}
  </ToggleSpan>
));

CustomToggle.displayName = 'CustomToggle';

const ToggleSpan = styled.span`
  cursor: pointer;
  display: inline-block;
`;

const AddToCalendar = ({
  name,
  description = "",
  location = "",
  startDate,
  endDate,
  startTime,
  endTime,
  timeZone = "America/New_York",
  children,
  ariaLabel,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsSmScreenDown();
  const [show, setShow] = React.useState(false);

  // Helper function to format date/time for different calendar services
  const formatDateTime = (date, time) => {
    const dateTime = `${date}T${time}:00`;
    const dt = new Date(dateTime);

    // For Google/Yahoo: Format as YYYYMMDDTHHmmssZ (UTC)
    const utcString = dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    // For Outlook: ISO format
    const isoString = dt.toISOString();

    // For ICS: Format as YYYYMMDDTHHmmss (local time)
    const localString = dateTime.replace(/[-:]/g, '');

    return { utcString, isoString, localString };
  };

  const start = formatDateTime(startDate, startTime, timeZone);
  const end = (endDate && endTime) ? formatDateTime(endDate, endTime, timeZone) : start;

  // Strip markdown syntax to get plain text for calendar services that don't render markdown
  const stripMarkdown = (md) =>
    md
      .replace(/!\[.*?\]\(.*?\)/g, '')   // images
      .replace(/#{1,6}\s*/g, '')          // headings
      .replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
      .replace(/(\*|_)(.*?)\1/g, '$2')    // italic
      .replace(/`{1,3}[^`]*`{1,3}/g, '')  // code
      .replace(/^\s*[-*+]\s+/gm, '')      // list items
      .replace(/\n{2,}/g, ' ')            // multiple newlines → space
      .replace(/\n/g, ' ')                // single newlines → space
      .trim();

  const plainDescription = stripMarkdown(description);

  // Track calendar clicks
  const trackCalendarClick = (calendarType) => {
    if (process.env.REACT_APP_GA_TRACKING === "true" && window.dataLayer) {
      window.dataLayer.push({
        event: 'calendar_add',
        calendar_type: calendarType,
        event_name: name,
      });
    }
  };

  // Generate calendar URLs
  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(name)}&dates=${start.utcString}/${end.utcString}&details=${encodeURIComponent(plainDescription)}&location=${encodeURIComponent(location)}`;

  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(name)}&startdt=${start.isoString}&enddt=${end.isoString}&body=${encodeURIComponent(plainDescription)}&location=${encodeURIComponent(location)}`;

  const yahooUrl = `https://calendar.yahoo.com/?v=60&title=${encodeURIComponent(name)}&st=${start.utcString}&et=${end.utcString}&desc=${encodeURIComponent(plainDescription)}&in_loc=${encodeURIComponent(location)}`;

  // Generate ICS file content
  const generateICS = () => {
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Playground//AddToCalendar//EN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@playground.ca`,
      `DTSTART:${start.localString}`,
      `DTEND:${end.localString}`,
      `SUMMARY:${name}`,
      `DESCRIPTION:${plainDescription.replace(/\n/g, '\\n')}`,
      `LOCATION:${location}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    return icsContent;
  };

  // Download ICS file for Apple
  const downloadICS = (e) => {
    e.preventDefault();
    trackCalendarClick('apple');
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download generic ICS file
  const downloadGenericICS = (e) => {
    e.preventDefault();
    trackCalendarClick('download');
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date and time for display in header
  const displayDate = formatDate(startDate, i18n.language, true);
  const displayTime = formatTime(`${startDate}T${startTime}`, i18n.language);

  // Configure Popper for mobile modal behavior
  const popperConfig = isMobile ? {
    strategy: 'fixed',
    modifiers: [
      {
        name: 'preventOverflow',
        enabled: false,
      },
      {
        name: 'flip',
        enabled: false,
      },
      {
        name: 'computeStyles',
        options: {
          adaptive: false,
        },
      },
    ],
  } : undefined;

  return (
    <StyledDropdown show={show} onToggle={setShow}>
      <Dropdown.Toggle as={CustomToggle} ariaLabel={ariaLabel}>
        {children}
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" popperConfig={popperConfig}>
        <Dropdown.Header className="main-header">
          <FaCalendarPlus /> {t("calendar.addToCalendar.label")}
          <button className="close-button" onClick={() => setShow(false)} aria-label="Close">
            <TiTimes />
          </button>
        </Dropdown.Header>
        <Dropdown.Divider />
        <Dropdown.Header>
          <div className="event-name">{name}</div>
          <div className="event-datetime">
            {displayDate} {t("calendar.at")} {displayTime}
          </div>
        </Dropdown.Header>
        <Dropdown.Divider />
        <Dropdown.Item onClick={downloadICS}>
          <FaApple className="calendar-icon" /> {t("calendar.addToCalendar.apple")}
        </Dropdown.Item>
        <Dropdown.Item
          href={googleCalendarUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('google')}
        >
          <FaGoogle className="calendar-icon" /> {t("calendar.addToCalendar.google")}
        </Dropdown.Item>
        <Dropdown.Item
          href={outlookUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('outlook')}
        >
          <FaMicrosoft className="calendar-icon" /> {t("calendar.addToCalendar.outlook")}
        </Dropdown.Item>
        <Dropdown.Item
          href={yahooUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('yahoo')}
        >
          <FaYahoo className="calendar-icon" /> {t("calendar.addToCalendar.yahoo")}
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={downloadGenericICS}>
          <FaDownload className="calendar-icon" /> {t("calendar.addToCalendar.download")}
        </Dropdown.Item>
      </Dropdown.Menu>
    </StyledDropdown>
  );
};

const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  position: relative;

  &.show {
    z-index: 10000;

    @media (max-width: 767px) {
      &::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 9999;
      }
    }
  }

  > span:first-child {
    display: flex !important;
    align-items: center !important;
  }

  .dropdown-toggle {
    display: flex;
    align-items: center;

    &::after {
      display: none;
    }
  }

  .dropdown-menu {
    background-color: var(--white);
    border: 1px solid var(--lightgrey);
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    min-width: 350px;
    z-index: 10001 !important;

    @media (max-width: 767px) {
      &.show {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        right: auto !important;
        bottom: auto !important;
        transform: translate(-50%, -50%) !important;
        width: 90% !important;
        max-width: 450px !important;
        max-height: 80vh !important;
        overflow-y: auto !important;
        margin: 0 !important;
        z-index: 10000 !important;
        border-radius: 8px !important;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
      }
    }

    .dropdown-header {
      font-weight: 700;
      font-size: 1rem;
      color: var(--black);
      padding: 0.75rem 1.25rem;
      white-space: normal;
      word-wrap: break-word;

      &.main-header {
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        position: relative;

        svg {
          font-size: 1rem;
          flex-shrink: 0;
          position: relative;
          top: -1px;
        }

        .close-button {
          position: absolute;
          top: 0.35rem;
          right: 0.5rem;
          background: none;
          border: none;
          padding: 0.25rem;
          cursor: pointer;
          color: var(--black);
          font-size: 1.2rem;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.7;

          &:hover {
            color: var(--red);
            opacity: 1;
          }
        }
      }

      .event-name {
        font-weight: 700;
        font-size: 1rem;
        margin-bottom: 0.5rem;
      }

      .event-datetime {
        font-weight: 400;
        font-size: 0.85rem;
        color: #666;
        display: flex;
        align-items: center;
        gap: 0.35rem;

        svg {
          font-size: 1rem;
          flex-shrink: 0;
        }
      }
    }

    .dropdown-item {
      padding: 0.75rem 1.25rem;
      transition: background 0.2s ease-in-out;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: var(--bluelinklight);

      .calendar-icon {
        font-size: 1.1rem;
        flex-shrink: 0;
        color: var(--black);
      }

      &:hover,
      &:focus {
        background-color: var(--red);
        color: var(--white);
      }

      &:active {
        background-color: var(--red);
        color: var(--white);
      }
    }

    .dropdown-divider {
      border-top: 1px solid #b5b5b5;
      margin: 0.5rem 0;
    }
  }
`;

AddToCalendar.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  location: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string,
  timeZone: PropTypes.string,
  children: PropTypes.node.isRequired,
  ariaLabel: PropTypes.string,
};

CustomToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default AddToCalendar;

